export interface CoachCallTranscriptLinkIdentity {
  coach_call_id: string;
  transcript_upload_id: string;
  leader_id: string | null;
  link_type: string;
}

export interface CoachCallTranscriptIdentity {
  id: string;
  leader_id: string | null;
}

export interface CoachCallParticipantIdentity {
  coach_call_id: string;
  leader_id: string;
}

export interface CoachCallArtifactIntakeIdentity {
  source_object_type: string;
  source_object_id: string | null;
  leader_id: string | null;
  transcript_upload_id: string | null;
  status: string;
  lifecycle_status: string;
}

interface CanonicalAutomaticIdentity {
  coachCallId: string;
  leaderId: string;
  transcriptUploadId: string;
}

function nonBlank(value: string | null | undefined): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function identityKey(identity: CanonicalAutomaticIdentity): string {
  return `${identity.coachCallId}:${identity.leaderId}:${identity.transcriptUploadId}`;
}

export function filterCanonicalCoachCallTranscriptLinks<
  TLink extends CoachCallTranscriptLinkIdentity,
>(params: {
  links: readonly TLink[];
  transcripts: readonly CoachCallTranscriptIdentity[];
  participants: readonly CoachCallParticipantIdentity[];
  artifactIntakes: readonly CoachCallArtifactIntakeIdentity[];
}): TLink[] {
  const transcriptsById = new Map(
    params.transcripts
      .filter((transcript) => nonBlank(transcript.id) && nonBlank(transcript.leader_id))
      .map((transcript) => [transcript.id, transcript]),
  );
  const participantPairs = new Set(
    params.participants
      .filter((participant) => nonBlank(participant.coach_call_id) && nonBlank(participant.leader_id))
      .map((participant) => `${participant.coach_call_id}:${participant.leader_id}`),
  );

  const automaticIdentitiesByCallLeader = new Map<string, Map<string, CanonicalAutomaticIdentity>>();
  for (const intake of params.artifactIntakes) {
    if (
      intake.source_object_type !== "coach_call" ||
      intake.status !== "ready" ||
      intake.lifecycle_status !== "processed" ||
      !nonBlank(intake.source_object_id) ||
      !nonBlank(intake.leader_id) ||
      !nonBlank(intake.transcript_upload_id) ||
      !participantPairs.has(`${intake.source_object_id}:${intake.leader_id}`)
    ) {
      continue;
    }

    const transcript = transcriptsById.get(intake.transcript_upload_id);
    if (transcript && transcript.leader_id !== intake.leader_id) continue;

    const identity: CanonicalAutomaticIdentity = {
      coachCallId: intake.source_object_id,
      leaderId: intake.leader_id,
      transcriptUploadId: intake.transcript_upload_id,
    };
    const callLeaderKey = `${identity.coachCallId}:${identity.leaderId}`;
    const identities = automaticIdentitiesByCallLeader.get(callLeaderKey) ?? new Map();
    identities.set(identityKey(identity), identity);
    automaticIdentitiesByCallLeader.set(callLeaderKey, identities);
  }

  return params.links.filter((link) => {
    if (
      !nonBlank(link.coach_call_id) ||
      !nonBlank(link.transcript_upload_id) ||
      !nonBlank(link.leader_id)
    ) {
      return false;
    }

    const transcript = transcriptsById.get(link.transcript_upload_id);
    if (
      !transcript ||
      transcript.leader_id !== link.leader_id ||
      !participantPairs.has(`${link.coach_call_id}:${link.leader_id}`)
    ) {
      return false;
    }

    if (link.link_type === "manual") return true;
    if (link.link_type !== "soft") return false;

    const identities = automaticIdentitiesByCallLeader.get(
      `${link.coach_call_id}:${link.leader_id}`,
    );
    if (!identities || identities.size !== 1) return false;
    return identities.has(`${link.coach_call_id}:${link.leader_id}:${link.transcript_upload_id}`);
  });
}

export function resolveCanonicalAutomaticCoachCallArtifact(params: {
  coachCallId: string;
  transcripts: readonly CoachCallTranscriptIdentity[];
  participants: readonly CoachCallParticipantIdentity[];
  artifactIntakes: readonly CoachCallArtifactIntakeIdentity[];
}): CanonicalAutomaticIdentity | null {
  if (!nonBlank(params.coachCallId)) return null;
  const candidateLinks = params.artifactIntakes.flatMap((intake) => {
    if (
      intake.source_object_type !== "coach_call" ||
      intake.source_object_id !== params.coachCallId ||
      !nonBlank(intake.leader_id) ||
      !nonBlank(intake.transcript_upload_id)
    ) {
      return [];
    }
    return [{
      coach_call_id: params.coachCallId,
      transcript_upload_id: intake.transcript_upload_id,
      leader_id: intake.leader_id,
      link_type: "soft",
    }];
  });
  const validLinks = filterCanonicalCoachCallTranscriptLinks({
    links: candidateLinks,
    transcripts: params.transcripts,
    participants: params.participants,
    artifactIntakes: params.artifactIntakes,
  });
  const identities = new Map<string, CanonicalAutomaticIdentity>();
  for (const link of validLinks) {
    const identity: CanonicalAutomaticIdentity = {
      coachCallId: link.coach_call_id,
      leaderId: link.leader_id,
      transcriptUploadId: link.transcript_upload_id,
    };
    identities.set(identityKey(identity), identity);
  }
  if (identities.size !== 1) return null;
  return identities.values().next().value ?? null;
}
